import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import UserContextMenu from 'components/ui/UserContextMenu';
import NotificationBell from 'components/ui/NotificationBell';
import NotificationPanel from 'components/ui/NotificationPanel';
import { useAuth } from '../../contexts/AuthContext';

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/home-dashboard',
    icon: 'LayoutDashboard',
    tooltip: 'Financial overview and insights',
  },
  {
    label: 'Budget',
    path: '/budget-manager',
    icon: 'Wallet',
    tooltip: 'Monthly budget management',
  },
  {
    label: 'Insights',
    path: '/spending-insights-dashboard',
    icon: 'BarChart3',
    tooltip: 'Spending analytics and AI insights',
  },
  {
    label: 'Goals',
    path: '/financial-goals',
    icon: 'Target',
    tooltip: 'Financial goals and progress tracking',
  },
  {
    label: 'Scan',
    path: '/ar-scanner-interface',
    icon: 'ScanLine',
    tooltip: 'AR product scanner and budget impact',
  },
  {
    label: 'Setup',
    path: '/onboarding-dashboard',
    icon: 'Settings2',
    tooltip: 'Financial profile and onboarding',
  },
  {
    label: 'Settings',
    path: '/settings-profile',
    icon: 'Settings',
    tooltip: 'Profile, notifications, security & subscription',
  },
];

export default function PrimaryNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const { user, userProfile } = useAuth();

  const authUser = {
    name: userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email || '',
    role: userProfile?.role || user?.user_metadata?.role || 'consumer',
    avatar: userProfile?.avatar_url || '',
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location?.pathname === path;

  return (
    <>
      {/* Desktop Top Navigation */}
      <header
        className={`hidden md:flex fixed top-0 left-0 right-0 z-[100] h-16 items-center justify-between px-6 transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
          scrolled
            ? 'bg-card shadow-glow-md border-b border-[rgba(6,182,212,0.15)]'
            : 'bg-primary border-b border-[rgba(6,182,212,0.08)]'
        }`}
      >
        {/* Logo */}
        <Link
          to="/home-dashboard"
          className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm"
          aria-label="FinVisAR - Go to Dashboard"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#06B6D4] to-[#0891B2] shadow-glow-sm transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:shadow-glow-md group-hover:scale-105">
            <Icon name="Scan" size={20} color="#0F172A" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-700 text-lg text-[#F8FAFC] tracking-tight select-none">
            FinVis<span className="text-accent">AR</span>
          </span>
        </Link>

        {/* Nav Items */}
        <nav aria-label="Primary navigation" className="flex items-center gap-1">
          {NAV_ITEMS?.map((item) => (
            <button
              key={item?.path}
              onClick={() => navigate(item?.path)}
              title={item?.tooltip}
              aria-current={isActive(item?.path) ? 'page' : undefined}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary min-h-[44px] ${
                isActive(item?.path)
                  ? 'bg-[rgba(6,182,212,0.12)] text-accent shadow-glow-ring'
                  : 'text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[rgba(6,182,212,0.06)]'
              }`}
            >
              <Icon
                name={item?.icon}
                size={18}
                color={isActive(item?.path) ? 'var(--color-accent)' : 'currentColor'}
                strokeWidth={isActive(item?.path) ? 2.5 : 2}
              />
              <span>{item?.label}</span>
              {isActive(item?.path) && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-accent rounded-full" />
              )}
            </button>
          ))}
        </nav>

        {/* Right slot: NotificationBell + UserContextMenu */}
        <div id="nav-right-slot" className="flex items-center gap-2">
          <NotificationBell />
          <UserContextMenu user={authUser} />
        </div>
      </header>

      {/* Notification Panel (portal-like, rendered here so it's above everything) */}
      <NotificationPanel />

      {/* Mobile Bottom Tab Bar */}
      <nav
        aria-label="Mobile primary navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 z-[100] bg-card border-t border-[rgba(6,182,212,0.15)] shadow-glow-md"
      >
        <div className="flex items-center justify-around h-16 px-2">
          {NAV_ITEMS?.map((item) => (
            <button
              key={item?.path}
              onClick={() => navigate(item?.path)}
              aria-current={isActive(item?.path) ? 'page' : undefined}
              aria-label={item?.label}
              className={`flex flex-col items-center justify-center gap-1 flex-1 h-full min-w-[44px] rounded-lg transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card ${
                isActive(item?.path)
                  ? 'text-accent' :'text-[#94A3B8] hover:text-[#E2E8F0]'
              }`}
            >
              <div
                className={`p-1.5 rounded-lg transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
                  isActive(item?.path) ? 'bg-[rgba(6,182,212,0.12)]' : ''
                }`}
              >
                <Icon
                  name={item?.icon}
                  size={22}
                  color={isActive(item?.path) ? 'var(--color-accent)' : 'currentColor'}
                  strokeWidth={isActive(item?.path) ? 2.5 : 2}
                />
              </div>
              <span
                className={`text-[10px] font-medium font-caption leading-none transition-all duration-[250ms] ${
                  isActive(item?.path) ? 'text-accent' : 'text-[#94A3B8]'
                }`}
              >
                {item?.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
      {/* Desktop spacer */}
      <div className="hidden md:block h-16" aria-hidden="true" />
    </>
  );
}